import { CardElement } from "@stripe/react-stripe-js"
import type { Stripe, StripeCardElementOptions, StripeElements } from "@stripe/stripe-js"
import {
  type AdminRegistrationPlan,
  type AdminRegistrationSetupIntent,
  adminOnboardingAccountRequestSchema,
  adminPasswordActionRequestSchema,
} from "@ternary/contracts"
import { Radio, type WizardErrors } from "@ternary/ui"
import { type ReactElement, type ReactNode, useId, useLayoutEffect, useRef, useState } from "react"
import { DocsLink } from "./docs-link.js"

/**
 * The page around onboarding and registration, which sit outside the admin shell: a top bar with
 * the Ternary name and the docs link, over one centred column.
 */
export function SetupPage({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="admin-setup">
      <header className="admin-setup-bar">
        <span className="admin-setup-brand">
          <span aria-hidden="true" className="admin-setup-mark">
            <svg aria-hidden="true" fill="currentColor" height="18" viewBox="0 0 24 24" width="18">
              <circle cx="12" cy="6.5" r="3.5" />
              <circle cx="6" cy="17" r="3.5" />
              <circle cx="18" cy="17" r="3.5" />
            </svg>
          </span>
          Ternary
        </span>
        <DocsLink />
      </header>
      <main className="admin-setup-main">{children}</main>
    </div>
  )
}

/** An amount in minor units, such as 9900 USD as "$99.00". */
export function formatMoney(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { currency, style: "currency" }).format(amountMinor / 100)
}

/** How often a price bills: "month", or "3 months" for one billed every 3. */
export function period(interval: string, intervalCount = 1) {
  return intervalCount > 1 ? `${intervalCount} ${interval}s` : interval
}

/** A plan's name: Basic is the free plan, as on ternarydev.com; Premium the first paid one. */
export const PLAN_NAMES: Record<AdminRegistrationPlan["tier"], string> = {
  enterprise: "Enterprise",
  free: "Basic",
  pro: "Pro",
  starter: "Premium",
}

/**
 * P2: the plans to choose from, as radio cards, each with what it costs. Nothing is chosen for the
 * owner: a paid plan is never taken without a choice.
 */
export function PlanField({
  error,
  name = "registration-plan",
  onChange,
  plans,
  value,
}: {
  error: string | undefined
  /** The radios' group name, one per form. */
  name?: string
  onChange(priceId: string): void
  plans: AdminRegistrationPlan[]
  value: string | null
}) {
  return (
    <fieldset className="admin-fieldset">
      <legend className="tny-label">Plan</legend>
      <div className="admin-stack">
        {plans.map((plan) => (
          <Radio
            card
            checked={value === plan.priceId}
            hint={
              plan.tier === "free"
                ? "Free. No transactional fee."
                : `${formatMoney(plan.amountMinor, plan.currency)}/${period(plan.interval, plan.intervalCount)}, plus a ${plan.feePercent}% fee on each sale.`
            }
            key={plan.priceId}
            label={PLAN_NAMES[plan.tier]}
            name={name}
            onChange={() => onChange(plan.priceId)}
          />
        ))}
      </div>
      {error ? (
        <p className="tny-error" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}

export const PASSWORD_MESSAGE = "Use 8 or more characters, with a number and a special character."

/** The contract's password rules, as one message per field. */
export function passwordErrors(draft: { password: string; confirmPassword: string }): WizardErrors {
  const errors: WizardErrors = {}
  const parsed = adminPasswordActionRequestSchema.safeParse(draft)
  if (parsed.error?.issues.some((issue) => issue.path[0] === "password")) {
    errors.password = PASSWORD_MESSAGE
  }
  // Checked here as well, because the contract's match check waits until the password is valid.
  if (draft.password !== draft.confirmPassword) errors.confirmPassword = "Passwords must match."
  return errors
}

/** True for an address the onboarding account step accepts. */
export function isEmail(text: string): boolean {
  return adminOnboardingAccountRequestSchema.shape.email.safeParse(text).success
}

/**
 * A wizard step's errors, less each one whose field was edited since they were shown, so an error
 * clears as soon as its field changes and the next Continue checks it again.
 */
export function useErrorsUntilEdited<D extends Record<string, unknown>>(
  errors: WizardErrors,
  draft: D,
): WizardErrors {
  const [shown, setShown] = useState({ draft, errors })
  if (shown.errors !== errors) setShown({ draft, errors })
  const at = shown.errors === errors ? shown.draft : draft
  return Object.fromEntries(
    Object.entries(errors).filter(([key]) => Object.is(draft[key], at[key])),
  )
}

/**
 * Stripe draws the card in its own frame, which the page's custom properties never reach, so it
 * gets the token values where the field sits: a ThemeRoot sets them on its own element.
 */
export function cardOptions(field: Element): StripeCardElementOptions {
  const style = getComputedStyle(field)
  const token = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback
  return {
    style: {
      base: {
        color: token("--color-text", "#111318"),
        fontFamily: token("--font-sans", "sans-serif"),
        fontSize: "16px",
        "::placeholder": { color: token("--color-muted", "#5c6370") },
      },
      invalid: { color: token("--color-danger", "#b42318") },
    },
  }
}

/**
 * The card: Stripe's card field in an input's frame, or, without a publishable key (local and
 * test environments), the fixture card that the fixture payment method stands for.
 */
export function CardField({
  publishableKey,
  error,
}: {
  publishableKey: string | null
  error?: string | undefined
}): ReactElement {
  const id = useId()
  const ref = useRef<HTMLFieldSetElement>(null)
  // Read once the fieldset is in place, before the first paint.
  const [options, setOptions] = useState<StripeCardElementOptions | null>(null)
  useLayoutEffect(() => {
    if (ref.current) setOptions(cardOptions(ref.current))
  }, [])
  return (
    // The fixture card is named for what it is. The Stripe field takes its name from the legend.
    <fieldset
      aria-describedby={error ? `${id}-error` : undefined}
      aria-label={publishableKey ? undefined : "Fixture payment card"}
      className="tny-field admin-card-field"
      ref={ref}
    >
      <legend className="tny-label">Card</legend>
      {publishableKey ? (
        <div className="tny-input admin-card-input">
          {options ? <CardElement options={options} /> : null}
        </div>
      ) : (
        <div className="tny-input admin-fixture-card">
          <span>4242 4242 4242 4242</span>
          <span>12 / 34</span>
          <span>123</span>
        </div>
      )}
      {error ? (
        <p className="tny-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}

/** The order summary, which sits above the pay button: one row per item, then the total. */
export function OrderSummary({
  rows,
  total,
}: {
  rows: [string, string][]
  total: [string, string]
}): ReactElement {
  const id = useId()
  return (
    <section aria-labelledby={id} className="admin-summary">
      <h3 className="admin-heading" id={id}>
        Order summary
      </h3>
      <dl className="admin-summary-list">
        {rows.map(([term, value]) => (
          <div key={term}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </div>
        ))}
        <div className="admin-summary-total">
          <dt>{total[0]}</dt>
          <dd>{total[1]}</dd>
        </div>
      </dl>
    </section>
  )
}

/**
 * The payment method for the card: the fixture one when the setup intent carries it, or the one
 * Stripe returns once it confirms the card. Throws with the message to show.
 */
export async function cardPaymentMethod(
  setup: AdminRegistrationSetupIntent | undefined,
  stripe: Stripe | null,
  elements: StripeElements | null,
): Promise<string> {
  if (!setup) throw new Error("Secure card entry is still loading.")
  if (setup.fixturePaymentMethodId) return setup.fixturePaymentMethodId
  const card = elements?.getElement(CardElement)
  if (!stripe || !card) throw new Error("Secure card entry is still loading.")
  const confirmed = await stripe.confirmCardSetup(setup.clientSecret, { payment_method: { card } })
  if (confirmed.error) throw new Error(confirmed.error.message ?? "The card could not be verified.")
  const method = confirmed.setupIntent.payment_method
  const id = typeof method === "string" ? method : method?.id
  if (!id) throw new Error("Stripe did not return a payment method.")
  return id
}
